import React, { useCallback } from "react";
import { map } from "lodash";
import Dropdown from "@radarly/components/panels/card-edition/sidebar/dropdown";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { useCurrentProjectFocusesId } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { useCurrentWorkspaceId } from "@radarly/core/workspace/use-cases";
import { tr } from "@radarly/i18n";
import { Card, CardWithTerm } from "@radarly/models/cards";
import {
  editDatavizOptionsOnCard,
  setCardTerm,
} from "@radarly/models/cards/editor";
import {
  TermSelectionMethod,
  TermSelectionMethods,
  changeTermSelectionMethod,
  getTermSelectionAndType,
  termHasSelectionMethods,
} from "@radarly/models/cards/form";
import { isHiddenTermsCard } from "@radarly/models/cards/is";
import {
  CardTerm,
  TermCardTerm,
  getTermFromCard,
} from "@radarly/models/cards/term";
import { Dataviz, isCloudDataviz } from "@radarly/models/dataviz";

export interface SharedPanelsCardEditionSidebarTermSortProps<
  D extends Dataviz
> {
  card: Card;
  setCard: (card: Card) => void;
  dataviz: D;
  term: TermCardTerm;
}

export default function SharedPanelsCardEditionSidebarTermSort<
  D extends Dataviz
>({
  card,
  setCard,
  dataviz,
  term,
}: SharedPanelsCardEditionSidebarTermSortProps<D>) {
  const locale = useCurrentUserLocale();
  const workspaceId = useCurrentWorkspaceId();
  const projectFocuses = useCurrentProjectFocusesId();
  const facets = useFacetsFromContext();

  const cardSelectionAndType = getTermSelectionAndType(getTermFromCard(card));
  const cardSelection = cardSelectionAndType[0] || "frequency";

  const updateCardTerm = useCallback(
    (nextTerm: CardTerm) => {
      let cleanCard = card;
      if (isHiddenTermsCard(card)) {
        cleanCard = editDatavizOptionsOnCard(card, {
          ...card.dataviz,
          hiddenTerms: [],
        });
      }
      return setCard(
        setCardTerm(
          workspaceId,
          projectFocuses,
          cleanCard as CardWithTerm,
          nextTerm as CardTerm,
          facets
        )
      );
    },
    [card, facets, projectFocuses, setCard, workspaceId]
  );

  if (isCloudDataviz(dataviz) && termHasSelectionMethods(term)) {
    return (
      <Dropdown
        label={tr(
          locale,
          "CUSTOM_PAGES::EDITOR::DATAVIZ::TERM_SELECTION_METHOD_TITLE"
        )}
        onChange={({ value }) => {
          const nextTerm = changeTermSelectionMethod(
            term,
            value as TermSelectionMethod
          );
          updateCardTerm(nextTerm as CardTerm);
        }}
        value={{
          value: cardSelection,
          label: tr(
            locale,
            "CUSTOM_PAGES::EDITOR::DATAVIZ::TERM_SELECTION_METHOD_" +
              cardSelection
          ),
        }}
        options={map(TermSelectionMethods, (unit) => ({
          value: unit,
          label: tr(
            locale,
            "CUSTOM_PAGES::EDITOR::DATAVIZ::TERM_SELECTION_METHOD_" + unit
          ),
        }))}
      />
    );
  }

  return null;
}
